import { P2GBox, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { useMeasurementSystem } from '~/hooks/use-measurement-system';
import { formatWeightString, formatDimensionsString } from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { ArchivedOrder } from './types';

interface props {
  order: ArchivedOrder;
}

const AddressBlock = ({
  size,
  title,
  name,
  organisation,
  line1,
  line2,
  line3,
  town,
  county,
  postcode,
  country,
  email,
  telephone
}: {
  size: number;
  title: string;
  name: string;
  organisation: string;
  line1: string;
  line2: string;
  line3: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
  email: string;
  telephone: string;
}) => (
  <P2GGrid item xs={12} sm={size}>
    <P2GText component="div" fontWeight="bold">
      {title}
    </P2GText>
    <P2GText component="div" variant="body2">
      {name}
    </P2GText>
    {organisation && (
      <P2GText component="div" variant="body2">
        {organisation}
      </P2GText>
    )}
    <P2GText component="div" variant="body2">
      {line1}
    </P2GText>
    {line2 && (
      <P2GText component="div" variant="body2">
        {line2}
      </P2GText>
    )}
    {line3 && (
      <P2GText component="div" variant="body2">
        {line3}
      </P2GText>
    )}
    <P2GText component="div" variant="body2">
      {town}
    </P2GText>
    {county && (
      <P2GText component="div" variant="body2">
        {county}
      </P2GText>
    )}
    <P2GText component="div" variant="body2">
      {postcode}
    </P2GText>
    <P2GText component="div" variant="body2" mb={1}>
      {country}
    </P2GText>
    {email && (
      <P2GText component="div" variant="body2">
        {email}
      </P2GText>
    )}
    {telephone && (
      <P2GText component="div" variant="body2">
        {telephone}
      </P2GText>
    )}
  </P2GGrid>
);

export const ArchivedOrderDetails: React.FC<props> = ({ order }) => {
  const { t } = useTranslation(TranslationNamespaces.MYORDERS);
  const { unit } = useMeasurementSystem();

  return (
    <P2GGrid container spacing={2} pt={2}>
      <AddressBlock
        size={4}
        title={t('ARCHIVED_ORDER_COLLECTION_DETAILS', { defaultValue: 'Collection details' })}
        name={order.CollectionName}
        organisation={order.CollectionOrganisation}
        line1={order.CollectionAddressLine1}
        line2={order.CollectionAddressLine2}
        line3={order.CollectionAddressLine3}
        town={order.CollectionTown}
        county={order.CollectionCounty}
        postcode={order.CollectionPostcode}
        country={order.CollectionCountry}
        email={order.CollectionEmail}
        telephone={order.CollectionTelephone}
      />
      <AddressBlock
        size={3}
        title={t('ARCHIVED_ORDER_DELIVERY_DETAILS', { defaultValue: 'Delivery details' })}
        name={order.DeliveryName}
        organisation={order.DeliveryOrganisation}
        line1={order.DeliveryAddressLine1}
        line2={order.DeliveryAddressLine2}
        line3={order.DeliveryAddressLine3}
        town={order.DeliveryTown}
        county={order.DeliveryCounty}
        postcode={order.DeliveryPostcode}
        country={order.DeliveryCountry}
        email={order.DeliveryEmail}
        telephone={order.DeliveryTelephone}
      />
      <P2GGrid item xs={12} sm={3}>
        <P2GText component="div" fontWeight="bold">
          {t('ARCHIVED_ORDER_PARCEL_DETAILS', { defaultValue: 'Parcel details' })}
        </P2GText>
        <P2GBox>
          <P2GText component="div" variant="body2">
            {order.Description}
          </P2GText>
          <P2GText component="div" variant="body2">
            {t('ARCHIVED_ORDER_QUANTITY', { defaultValue: 'Quantity: {{quantity}}', quantity: order.Quantity })}
          </P2GText>
          <P2GText component="div" variant="body2">
            {t('ARCHIVED_ORDER_VALUE', {
              defaultValue: 'Value: {{value}}',
              value: toDisplayCurrency(undefined, order.Value)
            })}
          </P2GText>
          <P2GText component="div" variant="body2">
            {t('ARCHIVED_ORDER_DIMENSIONS', {
              defaultValue: 'Dimensions: {{dimensions}}',
              dimensions: formatDimensionsString(
                { length: order.Length, width: order.Width, height: order.Height },
                unit
              )
            })}
          </P2GText>
          <P2GText component="div" variant="body2">
            {t('ARCHIVED_ORDER_WEIGHT', {
              defaultValue: 'Weight: {{weight}}',
              weight: formatWeightString(order.Weight, unit)
            })}
          </P2GText>
        </P2GBox>
      </P2GGrid>
    </P2GGrid>
  );
};
